import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import CtaBand from './CtaBand';
import './Pages.css';
import './Pflegekosten.css';
import usePageMeta, {breadcrumbJsonLd} from '../seo/usePageMeta';

const pflegekostenBreadcrumb = breadcrumbJsonLd([
  {name: 'Startseite', path: '/'},
  {name: 'Pflegekosten', path: '/pflegekosten'}
]);

// Rechenbeispiel mit gerundeten Beispielpreisen je Einsatz. Die tatsächlichen
// Preise richten sich nach der mit den Pflegekassen vereinbarten Vergütung.
const beispiel = {
  tage: 30,
  einsaetze: [
    {label: 'Morgens: Große Körperpflege, Hilfe beim Anziehen', preis: 38},
    {label: 'Abends: Kleine Körperpflege, Hilfe beim Zubettgehen', preis: 22}
  ],
  sachleistung: 1497
};

const euro = (value) => `${value.toLocaleString('de-DE')} €`;

const Pflegekosten = () => {
  usePageMeta({
    title: 'Pflegekosten und Pflegegrade 2026 | Cura Care Eitorf',
    description: 'Wer zahlt die ambulante Pflege? Pflegesachleistung, Pflegegeld, Entlastungsbetrag und Verhinderungspflege 2026 auf einen Blick. Cura Care rechnet direkt mit allen Kassen ab.',
    path: '/pflegekosten',
    jsonLd: pflegekostenBreadcrumb
  });

  const leistungen2026 = [
    { grad: '1', sachleistung: '–', pflegegeld: '–', tagespflege: '–', entlastung: '131 €' },
    { grad: '2', sachleistung: '796 €', pflegegeld: '347 €', tagespflege: '721 €', entlastung: '131 €' },
    { grad: '3', sachleistung: '1.497 €', pflegegeld: '599 €', tagespflege: '1.357 €', entlastung: '131 €' },
    { grad: '4', sachleistung: '1.859 €', pflegegeld: '800 €', tagespflege: '1.685 €', entlastung: '131 €' },
    { grad: '5', sachleistung: '2.299 €', pflegegeld: '990 €', tagespflege: '2.085 €', entlastung: '131 €' }
  ];

  const proTag = beispiel.einsaetze.reduce((sum, e) => sum + e.preis, 0);
  const proMonat = proTag * beispiel.tage;
  const eigenanteil = Math.max(proMonat - beispiel.sachleistung, 0);

  const weitereLeistungen = [
    { grad: '1', kurzzeitpflege: '–', vollstationaer: '–' },
    { grad: '2', kurzzeitpflege: '3.539 €', vollstationaer: '805 €' },
    { grad: '3', kurzzeitpflege: '3.539 €', vollstationaer: '1.319 €' },
    { grad: '4', kurzzeitpflege: '3.539 €', vollstationaer: '1.855 €' },
    { grad: '5', kurzzeitpflege: '3.539 €', vollstationaer: '2.096 €' }
  ];

  return (
    <div className="page-view pflegekosten-page">
      <Header />

      <main className="page pflegekosten-page__main" id="pflegekosten">
        <section className="pflegekosten">
          <div className="container">
            <nav className="breadcrumb" aria-label="Brotkrumen">
              <Link to="/">Startseite</Link>
              <span aria-hidden="true">/</span>
              <span>Pflegekosten</span>
            </nav>

            <div className="pflegekosten__header">
              <span className="pflegekosten__eyebrow">Transparenz &amp; Finanzierung</span>
              <h1>Pflegekosten</h1>
              <p>
                Wir sind Vertragspartner aller gesetzlichen Pflege- und Krankenkassen
                und rechnen direkt mit den Kostenträgern ab.
              </p>
            </div>

            <div className="pflegekosten__card">
              <h2>Kostenträger</h2>
              <p>Folgende Kostenträger sind möglich:</p>
              <ul className="pflegekosten__list pflegekosten__list--grid">
                <li>Krankenkasse</li>
                <li>Pflegekasse</li>
                <li>Beihilfe</li>
                <li>Selbstzahler</li>
              </ul>
              <p>Auch mehrere Kostenträger können gleichzeitig in Anspruch genommen werden.</p>
            </div>

            <div className="pflegekosten__grid">
              <div className="pflegekosten__card">
                <h2>Die Pflegeversicherung</h2>
                <p>
                  Leistungen der Pflegeversicherung werden auf Antrag gewährt.
                  Voraussetzung ist, dass Pflegebedürftigkeit festgestellt und
                  ein Pflegegrad anerkannt wird.
                </p>

                <h3>Antragstellung</h3>
                <p>
                  Der Antrag kann formlos oder über ein Formular bei der Pflegekasse
                  gestellt werden. Entscheidend für den Leistungsbeginn ist das
                  Eingangsdatum.
                </p>
              </div>

              <div className="pflegekosten__card">
                <h2>Begutachtung &amp; Pflegegrad</h2>
                <p>
                  Nach Antragstellung beauftragt die Pflegekasse den Medizinischen
                  Dienst (MD) mit einer Begutachtung im häuslichen Umfeld.
                </p>
                <p>Geprüft werden unter anderem diese Bereiche:</p>
                <ul className="pflegekosten__list">
                  <li>Mobilität</li>
                  <li>Kognitive und kommunikative Fähigkeiten</li>
                  <li>Verhaltensweisen und psychische Problemlagen</li>
                  <li>Selbstversorgung</li>
                  <li>Krankheits- und therapiebedingte Anforderungen</li>
                  <li>Alltagsgestaltung und soziale Kontakte</li>
                </ul>
              </div>
            </div>

            <div className="pflegekosten__card">
              <h2>Widerspruch &amp; Höherstufung</h2>
              <p>
                Gegen den Bescheid der Pflegekasse kann innerhalb von vier Wochen
                schriftlich Widerspruch eingelegt werden. Auch eine Höherstufung
                kann beantragt werden, wenn sich der Gesundheitszustand verändert.
              </p>
              <p>Gerne unterstützen wir Sie dabei persönlich.</p>
            </div>

            <div className="pflegekosten__card">
              <h2>Leistungen ab 2026</h2>
              <p className="pflegekosten__table-hint">
                Auf kleineren Bildschirmen kann die Tabelle horizontal gescrollt werden.
              </p>
              <div className="pflegekosten__table-wrap">
                <table className="pflegekosten__table">
                  <thead>
                    <tr>
                      <th>Pflegegrad</th>
                      <th>Pflegesachleistung</th>
                      <th>Pflegegeld</th>
                      <th>Tages- / Nachtpflege</th>
                      <th>Entlastungsbetrag</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leistungen2026.map((item) => (
                      <tr key={item.grad}>
                        <td>{item.grad}</td>
                        <td>{item.sachleistung}</td>
                        <td>{item.pflegegeld}</td>
                        <td>{item.tagespflege}</td>
                        <td>{item.entlastung}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="pflegekosten__card pflegekosten__example">
              <h2>Rechenbeispiel: Was bleibt selbst zu zahlen?</h2>
              <p>
                Frau M. hat Pflegegrad 3 und wird von uns zweimal täglich bei der Grundpflege
                unterstützt.
              </p>
              <ul className="pflegekosten__calc">
                {beispiel.einsaetze.map((e) => (
                  <li key={e.label}>
                    <span>{e.label}</span>
                    <span>{euro(e.preis)}</span>
                  </li>
                ))}
                <li className="pflegekosten__calc-sum">
                  <span>Pflege im Monat ({beispiel.tage} Tage × {euro(proTag)})</span>
                  <span>{euro(proMonat)}</span>
                </li>
                <li className="pflegekosten__calc-kasse">
                  <span>Zahlt die Pflegekasse (Pflegesachleistung Pflegegrad 3)</span>
                  <span>− {euro(beispiel.sachleistung)}</span>
                </li>
                <li className="pflegekosten__calc-result">
                  <span>Eigenanteil im Monat</span>
                  <span>{euro(eigenanteil)}</span>
                </li>
              </ul>
              <p>
                Die Pflegekasse rechnet direkt mit uns ab, Sie erhalten nur die Rechnung über den
                Eigenanteil. Ärztlich verordnete Behandlungspflege, etwa das Anziehen von
                Kompressionsstrümpfen, zahlt zusätzlich die Krankenkasse. Sie belastet dieses
                Budget nicht. Wird das Budget nicht ganz ausgeschöpft, zahlt die Pflegekasse den
                Rest anteilig als Pflegegeld aus.
              </p>
              <p className="pflegekosten__table-hint">
                Die Preise sind gerundete Beispielwerte. Was in Ihrer Situation tatsächlich
                anfällt, rechnen wir Ihnen im kostenlosen Beratungsgespräch genau aus.
              </p>
            </div>

            <div className="pflegekosten__card">
              <h2>Weitere Leistungen</h2>
              <p className="pflegekosten__table-hint">
                Auch diese Tabelle kann auf dem Smartphone nach links und rechts gescrollt werden.
              </p>
              <div className="pflegekosten__table-wrap">
                <table className="pflegekosten__table">
                  <thead>
                    <tr>
                      <th>Pflegegrad</th>
                      <th>Verhinderungs- / Kurzzeitpflege</th>
                      <th>Vollstationäre Pflege</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weitereLeistungen.map((item) => (
                      <tr key={item.grad}>
                        <td>{item.grad}</td>
                        <td>{item.kurzzeitpflege}</td>
                        <td>{item.vollstationaer}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="pflegekosten__grid">
              <div className="pflegekosten__card">
                <h2>Kombinationsleistung</h2>
                <p>
                  Pflegesachleistungen und Pflegegeld können kombiniert werden,
                  wenn Angehörige einen Teil der Pflege übernehmen und ein
                  ambulanter Dienst ergänzend unterstützt.
                </p>
              </div>

              <div className="pflegekosten__card">
                <h2>Die Krankenkasse als Kostenträger</h2>
                <p>
                  Leistungen der häuslichen Krankenpflege werden in der Regel über
                  die Krankenkasse abgerechnet. Dafür ist meist eine ärztliche
                  Verordnung notwendig.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <CtaBand />

      <Footer />
    </div>
  );
};

export default Pflegekosten;
